// Instagram per-account connect — pure helpers shared by the callback page + the
// account API-Connections button. No I/O. Instagram is a STANDALONE OAuth in Zernio
// (its own login, no agency/manager model), so this is strictly per-account.
//
// STANDARD mode (not headless): Zernio redirects back to our SPA callback with
// `?connected=instagram&profileId=…&accountId=…&username=…` and creates the IG
// SocialAccount directly (no page/location sub-selection — confirmed from the SDK
// getConnectUrl doc: "If no selection is needed, the account is created directly
// and the redirect includes accountId"). We put OUR kyck account ref under a
// distinct `kyckAccountId` param so it never collides with Zernio's appended
// `accountId` (the IG SocialAccount id).

export interface InstagramCallbackParams {
  kyckAccountId: string | null;
  connected: string | null; // Zernio's `connected` flag (should be 'instagram')
  igAccountId: string | null; // Zernio's appended accountId = the IG SocialAccount id (display only)
  username: string | null; // the connected IG handle (display / name fallback)
  error: string | null; // present when the OAuth failed / was cancelled
}

function nonEmpty(v: string | null): string | null {
  return v && v.trim() !== '' ? v.trim() : null;
}

export function parseInstagramCallbackParams(params: URLSearchParams): InstagramCallbackParams {
  return {
    kyckAccountId: nonEmpty(params.get('kyckAccountId')),
    connected: nonEmpty(params.get('connected')),
    igAccountId: nonEmpty(params.get('accountId')),
    username: nonEmpty(params.get('username')),
    error: nonEmpty(params.get('error')),
  };
}

export type InstagramCallbackOutcome =
  | 'ready' // we have the kyck account ref — finalize (server re-syncs + captures)
  | 'missing-account' // no kyck account reference — the flow can't be resolved
  | 'error'; // Zernio returned an OAuth error

export function classifyInstagramCallback(p: InstagramCallbackParams): InstagramCallbackOutcome {
  if (p.error) return 'error';
  if (!p.kyckAccountId) return 'missing-account';
  return 'ready';
}

// Whether to render the per-account "Connect Instagram" button on the account's
// API-Connections row. Needs a Zernio profile (IG links onto it), a known status,
// and the platform currently not connected.
export function canOfferInstagramConnect(input: {
  hasProfile: boolean;
  statusUnknown: boolean;
  connected: boolean | null;
}): boolean {
  return input.hasProfile && !input.statusUnknown && input.connected === false;
}
